import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Plus, Loader2, Trash2, Coins } from "lucide-react";
import { toast } from "sonner";

export default function Currencies() {
  const [currencies, setCurrencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/currencies"); setCurrencies(data); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/currencies", { code });
      toast.success("Currency added");
      setCode(""); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    try { await api.delete(`/currencies/${c}`); toast.success("Currency removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="currencies-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">Currencies</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage the currencies employees can select on reimbursement claims. Default is NZD.</p>
      </div>

      <form onSubmit={add} className="bg-card border border-border rounded-md p-5 flex items-end gap-3 max-w-md">
        <div className="flex-1 space-y-2">
          <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Currency code</label>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. NZD" maxLength={5}
            className="rounded-sm font-mono uppercase" data-testid="currency-code" />
        </div>
        <Button type="submit" disabled={busy} className="rounded-sm" data-testid="currency-submit">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}Add
        </Button>
      </form>

      <div className="bg-card border border-border rounded-md">
        {loading ? (
          <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : currencies.length === 0 ? (
          <div className="p-12 text-center"><Coins className="h-10 w-10 mx-auto text-muted-foreground mb-3" /><p className="text-sm text-muted-foreground">No currencies yet.</p></div>
        ) : (
          <ul className="divide-y divide-border">
            {currencies.map((c) => (
              <li key={c} className="flex items-center justify-between px-5 py-3 hover:bg-muted/50 transition-colors" data-testid={`currency-row-${c}`}>
                <span className="flex items-center gap-3">
                  <Coins className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono font-medium">{c}</span>
                  {c === "NZD" && <span className="text-xs text-muted-foreground">(default)</span>}
                </span>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid={`currency-delete-${c}`}><Trash2 className="h-4 w-4 text-danger" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Remove {c}?</AlertDialogTitle>
                      <AlertDialogDescription>Employees will no longer be able to select this currency on new claims. Existing claims are unaffected.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="rounded-sm bg-danger hover:bg-danger/90" onClick={() => remove(c)} data-testid={`currency-confirm-delete-${c}`}>Remove</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
